import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { ProfessorNote } from '@/types'
import {
  StickyNote,
  Loader2,
  Search,
  ChevronRight,
  User,
  Calendar,
  Trash2,
  CheckCircle2,
  Circle
} from 'lucide-react'

interface NoteWithTeacher extends ProfessorNote {
  teacher_name: string
}

type FilterType = 'all' | 'pending' | 'completed'

export default function NotesList() {
  const navigate = useNavigate()
  const [notes, setNotes] = useState<NoteWithTeacher[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filter, setFilter] = useState<FilterType>('all')

  useEffect(() => {
    loadNotes()
  }, [])

  const loadNotes = async () => {
    try {
      const { data } = await (supabase
        .from('professor_notes') as any)
        .select('*, teacher:profiles!teacher_id(first_name, last_name)')
        .order('is_completed', { ascending: true })
        .order('created_at', { ascending: false })

      const mapped: NoteWithTeacher[] = (data || []).map((n: any) => ({
        ...n,
        teacher_name: n.teacher ? `${n.teacher.first_name} ${n.teacher.last_name}` : 'Desconocido'
      }))
      setNotes(mapped)
    } catch (err) {
      console.error('Error loading notes:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const getAdminEditUrl = (note: ProfessorNote): string => {
    if (note.exercise_id) return `/admin/exercises/${note.exercise_id}/edit?note_id=${note.id}`
    if (note.content_id) return `/admin/contents/${note.content_id}/edit?note_id=${note.id}`
    if (note.lesson_id) return `/admin/lessons/${note.lesson_id}/edit?note_id=${note.id}`
    if (note.unit_id) return `/admin/units/${note.unit_id}/edit?note_id=${note.id}`
    return `/admin/dashboard?note_id=${note.id}`
  }

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    if (!confirm('¿Eliminar esta nota?')) return
    await (supabase.from('professor_notes') as any).delete().eq('id', id)
    setNotes(prev => prev.filter(n => n.id !== id))
  }

  const handleToggleComplete = async (e: React.MouseEvent, note: NoteWithTeacher) => {
    e.stopPropagation()
    const newVal = !note.is_completed
    await (supabase.from('professor_notes') as any)
      .update({ is_completed: newVal })
      .eq('id', note.id)
    setNotes(prev => prev.map(n => n.id === note.id ? { ...n, is_completed: newVal } : n))
  }

  const totalCount = notes.length
  const pendingCount = notes.filter(n => !n.is_completed).length
  const completedCount = notes.filter(n => n.is_completed).length

  const filteredNotes = notes.filter(n => {
    const matchesSearch =
      n.location_label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      n.note.toLowerCase().includes(searchTerm.toLowerCase()) ||
      n.teacher_name.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesFilter =
      filter === 'all' ||
      (filter === 'pending' && !n.is_completed) ||
      (filter === 'completed' && n.is_completed)
    return matchesSearch && matchesFilter
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <StickyNote className="w-6 h-6 text-amber-500" />
          Notas del Super Prof.
        </h1>
        <p className="text-gray-600 mt-1">
          Notas generadas durante la previsualización del contenido. Haz clic para ir al punto exacto.
        </p>
      </div>

      {/* Filtros */}
      <div className="flex gap-2">
        {([
          { key: 'all', label: 'Todas', count: totalCount },
          { key: 'pending', label: 'Pendientes', count: pendingCount },
          { key: 'completed', label: 'Terminadas', count: completedCount },
        ] as { key: FilterType; label: string; count: number }[]).map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              filter === tab.key
                ? 'bg-amber-500 text-white'
                : 'bg-white border text-gray-600 hover:border-amber-300'
            }`}
          >
            {tab.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
              filter === tab.key ? 'bg-amber-400 text-white' : 'bg-gray-100 text-gray-500'
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Buscador */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar por ubicación, nota o profesor..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
        />
      </div>

      {/* Lista de notas */}
      {filteredNotes.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center shadow-sm">
          <StickyNote className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay notas{filter !== 'all' ? ' en esta categoría' : ''}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredNotes.map(note => (
            <button
              key={note.id}
              onClick={() => navigate(getAdminEditUrl(note))}
              className={`w-full text-left bg-white rounded-xl shadow-sm border transition-all p-4 flex items-start gap-4 ${
                note.is_completed
                  ? 'opacity-60 border-gray-200 hover:border-gray-300'
                  : 'hover:border-amber-300 hover:shadow-md'
              }`}
            >
              {/* Botón completar */}
              <button
                onClick={(e) => handleToggleComplete(e, note)}
                className="shrink-0 mt-0.5 p-0.5 rounded-full hover:scale-110 transition-transform"
                title={note.is_completed ? 'Marcar como pendiente' : 'Marcar como terminada'}
              >
                {note.is_completed
                  ? <CheckCircle2 className="w-5 h-5 text-green-500" />
                  : <Circle className="w-5 h-5 text-gray-300" />
                }
              </button>

              {/* Label chip */}
              <div className="shrink-0 mt-0.5">
                <span className={`px-2.5 py-1 rounded-lg text-sm font-mono font-semibold ${
                  note.is_completed
                    ? 'bg-gray-100 text-gray-500 line-through'
                    : 'bg-amber-100 text-amber-800'
                }`}>
                  {note.location_label}
                </span>
              </div>

              {/* Contenido */}
              <div className="flex-1 min-w-0">
                <p className={`text-sm line-clamp-2 ${note.is_completed ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                  {note.note}
                </p>
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <User className="w-3.5 h-3.5" />
                    {note.teacher_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(note.created_at).toLocaleString('es-CL')}
                  </span>
                </div>
              </div>

              {/* Acciones */}
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={(e) => handleDelete(e, note.id)}
                  className="p-1.5 hover:bg-red-100 rounded-lg text-red-600 hover:text-red-600 transition-colors"
                  title="Eliminar nota"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </button>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-400 text-center">
        {filteredNotes.length} nota{filteredNotes.length !== 1 ? 's' : ''} — haz clic en cualquiera para ir al contenido
      </p>
    </div>
  )
}
